<template>
  <div class="nmc-panel">
    <div class="nmc-header">
      <div>
        <span class="nmc-title">Nuevo cargo</span>
        <span class="nmc-subtitle">Cargo adicional a un inquilino</span>
      </div>
      <button class="nmc-close-btn" @click="onCloseAttempt"><span class="mdi mdi-close"></span></button>
    </div>

    <div class="nmc-content">
      <!-- Grupo 1 -->
      <section class="nmc-group">
        <div class="nmc-group__header"><span class="nmc-group__number">1</span> ¿A quién se cobra?</div>

        <div v-if="!form.contractId" class="nmc-tenant-search">
          <input v-model="tenantQuery" class="nmc-input" placeholder="Buscar inquilino o nave..." @input="onTenantSearch" />
          <div v-if="tenantResults.length" class="nmc-tenant-results">
            <button v-for="t in tenantResults" :key="t.contractId" class="nmc-tenant-result" @click="selectTenant(t)">
              <span class="nmc-tenant-result-name">{{ t.tenantName }}</span>
              <span class="nmc-tenant-result-context">{{ t.plotName }} · Saldo {{ formatCurrency(t.pendingBalance) }}</span>
            </button>
          </div>
        </div>

        <div v-else class="nmc-tenant-card">
          <span class="nmc-tenant-avatar" :style="{ background: avatarColor(selectedTenant?.tenantName) }">{{ initials(selectedTenant?.tenantName) }}</span>
          <div class="nmc-tenant-info">
            <div class="nmc-tenant-name">{{ selectedTenant?.tenantName || 'Cargando inquilino...' }}</div>
            <div class="nmc-tenant-sub">{{ selectedTenant?.plotName }} · Saldo {{ formatCurrency(selectedTenant?.pendingBalance) }}</div>
          </div>
          <button class="nmc-tenant-clear" @click="clearTenant"><span class="mdi mdi-close"></span></button>
        </div>

        <div v-if="tenantPlots.length > 1" class="nmc-plot-pills">
          <button v-for="p in tenantPlots" :key="p.contractId" class="nmc-pill" :class="{ 'nmc-pill--active': form.contractId === p.contractId }" @click="form.contractId = p.contractId">
            {{ p.plotName }}
          </button>
        </div>

        <label class="nmc-field-label">Periodo</label>
        <select v-model="form.period" class="nmc-input">
          <option v-for="p in periodOptions" :key="p.value" :value="p.value">{{ p.label }}</option>
        </select>
      </section>

      <!-- Grupo 2 -->
      <section class="nmc-group">
        <div class="nmc-group__header"><span class="nmc-group__number">2</span> ¿Qué se cobra?</div>

        <div class="nmc-type-grid">
          <button class="nmc-type-pill" :class="{ 'nmc-type-pill--active': form.manualType === 'PENALTY' }" @click="form.manualType = 'PENALTY'">
            <span class="mdi mdi-alert-outline" style="color:#e84545"></span> Penalidad o daño
          </button>
          <button class="nmc-type-pill" :class="{ 'nmc-type-pill--active': form.manualType === 'SPECIAL_SERVICE' }" @click="form.manualType = 'SPECIAL_SERVICE'">
            <span class="mdi mdi-star-outline" style="color:#3b82f6"></span> Servicio especial
          </button>
          <button class="nmc-type-pill" :class="{ 'nmc-type-pill--active': form.manualType === 'BILLING_ADJUSTMENT' }" @click="form.manualType = 'BILLING_ADJUSTMENT'">
            <span class="mdi mdi-calculator-variant-outline" style="color:#f0a500"></span> Ajuste de facturación
          </button>
          <button class="nmc-type-pill" :class="{ 'nmc-type-pill--active': form.manualType === 'OTHER' }" @click="form.manualType = 'OTHER'">
            <span class="mdi mdi-plus" style="color:#94a3b8"></span> Otro concepto
          </button>
        </div>

        <input v-if="form.manualType === 'OTHER'" v-model="form.manualTypeOther" class="nmc-input" placeholder="Nombre del concepto" />
        <input v-if="form.manualType === 'PENALTY'" v-model="form.contractClauseRef" class="nmc-input" placeholder="Referencia a cláusula del contrato (opcional)" />

        <label class="nmc-field-label">Descripción del concepto</label>
        <textarea v-model="form.description" class="nmc-textarea" rows="3" :placeholder="descriptionPlaceholder"></textarea>
      </section>

      <!-- Grupo 3 -->
      <section class="nmc-group">
        <div class="nmc-group__header"><span class="nmc-group__number">3</span> ¿Cuánto y cuándo?</div>

        <div class="nmc-amount-row">
          <div class="nmc-amount-input-wrap">
            <span class="nmc-amount-prefix">$</span>
            <input v-model.number="form.amount" type="number" class="nmc-amount-input" placeholder="0.00" />
          </div>
          <select v-model="form.currency" class="nmc-input nmc-currency-select">
            <option value="MXN">MXN</option>
            <option value="USD">USD</option>
          </select>
        </div>

        <label class="nmc-toggle-row">
          <input type="checkbox" v-model="form.includeIva" /> Incluir IVA
          <input v-if="form.includeIva" v-model.number="form.ivaPercent" type="number" class="nmc-input nmc-iva-percent" placeholder="16" />
          <span v-if="form.includeIva">%</span>
        </label>
        <div v-if="form.includeIva && form.amount" class="nmc-iva-breakdown">
          Subtotal {{ formatCurrency(form.amount) }} + IVA {{ formatCurrency(form.amount * ((form.ivaPercent || 16) / 100)) }} = {{ formatCurrency(form.amount * (1 + (form.ivaPercent || 16) / 100)) }}
        </div>

        <label class="nmc-field-label">Fecha de vencimiento</label>
        <div class="nmc-pills">
          <button class="nmc-pill" :class="{ 'nmc-pill--active': dueOption === 'default' }" @click="setDueOption('default')">{{ defaultDueDays }} días (sugerido)</button>          <button class="nmc-pill" :class="{ 'nmc-pill--active': dueOption === '30' }" @click="setDueOption('30')">30 días</button>
          <button class="nmc-pill" :class="{ 'nmc-pill--active': dueOption === 'eom' }" @click="setDueOption('eom')">Fin de mes</button>
          <button class="nmc-pill" :class="{ 'nmc-pill--active': dueOption === 'custom' }" @click="dueOption = 'custom'">Personalizada</button>
        </div>
        <input v-if="dueOption === 'custom'" v-model="form.dueDate" type="date" class="nmc-input" />

        <label class="nmc-toggle-row">
          <input type="checkbox" v-model="form.latePenaltyEnabled" /> Aplicar penalidad por pago tardío
        </label>
        <div v-if="form.latePenaltyEnabled" class="nmc-penalty-fields">
          <input v-model.number="form.latePenaltyPercent" type="number" class="nmc-input" placeholder="% mensual" />
          <input v-model="form.latePenaltyStartDate" type="date" class="nmc-input" />
        </div>
      </section>

      <!-- Grupo 4 -->
      <section class="nmc-group">
        <div class="nmc-group__header"><span class="nmc-group__number">4</span> Documentación <span class="nmc-optional">(Opcional)</span></div>

        <div class="nmc-dropzone" @click="fileInputEl?.click()">
          <span class="mdi mdi-paperclip nmc-dropzone-icon"></span>
          <span class="nmc-dropzone-text">
            <span class="nmc-dropzone-title">Adjuntar documentos de soporte</span>
            <span class="nmc-dropzone-hint">Fotos de daños, cotizaciones, autorizaciones, órdenes de trabajo.</span>
          </span>
        </div>
        <input ref="fileInputEl" type="file" multiple style="display: none" @change="onFilesSelected" />

        <div v-if="attachments.length" class="nmc-attachments">
          <span v-for="(f, i) in attachments" :key="i" class="nmc-attachment-pill">
            {{ f.name.length > 20 ? f.name.slice(0, 17) + '...' : f.name }} · {{ Math.round(f.size / 1024) }}KB
            <span class="mdi mdi-close" @click="attachments.splice(i, 1)"></span>
          </span>
        </div>

        <label v-if="attachments.length" class="nmc-toggle-row">
          <input type="checkbox" v-model="form.includeAttachmentsInStatement" /> Incluir en estado de cuenta
        </label>

        <label class="nmc-field-label">Nota interna</label>
        <textarea v-model="form.internalNote" class="nmc-textarea" rows="2" placeholder="Contexto adicional para el equipo (no visible para el inquilino)..."></textarea>

        <label class="nmc-toggle-row">
          <input type="checkbox" v-model="form.notifyTenant" /> Notificar al inquilino al crear el cargo
        </label>
      </section>

      <div v-if="canSave" class="nmc-preview">
        <div class="nmc-preview__content">
          <span class="nmc-preview__type-tag" :class="`nmc-preview__type-tag--${form.manualType}`">{{ typeLabel }}</span>
          <span class="nmc-preview__desc">{{ form.description || '(sin descripción)' }}</span>
          <span class="nmc-preview__amount">{{ formatCurrency(finalAmount) }}</span>
          <span class="nmc-preview__due">Vence {{ formatDate(form.dueDate) }}</span>
        </div>
        <div class="nmc-preview__hint"><span class="mdi mdi-eye-outline"></span> Así verá el inquilino este cargo</div>
      </div>
    </div>

    <div class="nmc-footer">
      <button class="nmc-cancel-btn" @click="onCloseAttempt">Cancelar</button>
      <div class="nmc-submit-wrap">
        <button class="nmc-submit-btn" :disabled="!canSave || submitting" @click="onSubmit">{{ submitting ? 'Creando...' : 'Crear cargo' }}</button>
        <span v-if="!canSave" class="nmc-submit-hint">Selecciona inquilino, tipo de cargo y monto para continuar.</span>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { searchTenantsForCharge, getTenantPlots, createManualCharge, uploadChargeAttachment, getAccountDetail } from '@/services/charges'
import { getBillingConfig } from '@/services/parkConfig'
const props = defineProps({
  parkId: { type: String, required: true },
  period: { type: String, required: true },
  preselectedContractId: { type: String, default: null },
})

const emit = defineEmits(['close', 'created'])

const AVATAR_PALETTE = ['#3b82f6', '#10b981', '#8b5cf6', '#f97316', '#14b8a6', '#ec4899', '#6366f1', '#f0a500']
function avatarColor(name) {
  let hash = 0
  for (let i = 0; i < (name || '').length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash)
  return AVATAR_PALETTE[Math.abs(hash) % AVATAR_PALETTE.length]
}
function initials(name) { return (name || '').split(' ').map((w) => w[0]).slice(0, 2).join('').toUpperCase() }

const tenantQuery = ref('')
const tenantResults = ref([])
const selectedTenant = ref(null)
const tenantPlots = ref([])
let debounceHandle = null

async function onTenantSearch() {
  if (debounceHandle) clearTimeout(debounceHandle)
  if (!tenantQuery.value.trim()) { tenantResults.value = []; return }
  debounceHandle = setTimeout(async () => {
    tenantResults.value = await searchTenantsForCharge(props.parkId, tenantQuery.value)
  }, 250)
}

async function selectTenant(t) {
  selectedTenant.value = t
  form.value.contractId = t.contractId
  tenantResults.value = []
  tenantQuery.value = ''
  tenantPlots.value = await getTenantPlots(props.parkId, t.tenantId)
}

function clearTenant() {
  selectedTenant.value = null
  form.value.contractId = null
  tenantPlots.value = []
}

const periodOptions = (() => {
  const now = new Date()
  const MONTHS = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre']
  const opts = []
  for (let i = -3; i <= 1; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() + i, 1)
    opts.push({ value: `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`, label: `${MONTHS[d.getMonth()]} ${d.getFullYear()}` })
  }
  return opts
})()

const form = ref({
  contractId: null, period: props.period,
  manualType: null, manualTypeOther: '', contractClauseRef: '', description: '',
  amount: null, currency: 'MXN', includeIva: false, ivaPercent: 16,
  dueDate: '', latePenaltyEnabled: false, latePenaltyPercent: null, latePenaltyStartDate: '',
  internalNote: '', notifyTenant: true, includeAttachmentsInStatement: false,
})

const defaultDueDays = ref(15)

onMounted(async () => {
  if (props.preselectedContractId) {
    form.value.contractId = props.preselectedContractId
    try {
      const detail = await getAccountDetail(props.preselectedContractId, props.period)
      if (detail) {
        selectedTenant.value = {
          contractId: props.preselectedContractId,
          tenantName: detail.tenantName,
          plotName: detail.plotName,
          pendingBalance: detail.pendingCurrent,
        }
      }
    } catch (e) {
      console.error('No se pudo cargar el inquilino preseleccionado:', e)
    }
  }
  const config = await getBillingConfig(props.parkId)
  if (config?.manualChargeDueDaysDefault) {
    defaultDueDays.value = config.manualChargeDueDaysDefault
    if (dueOption.value === 'default') setDueOption('default')
  }
})

const dueOption = ref('default')
function setDueOption(opt) {
  dueOption.value = opt
  const now = new Date()
  if (opt === 'default') form.value.dueDate = new Date(now.getTime() + defaultDueDays.value * 24 * 60 * 60 * 1000).toISOString().slice(0, 10)
  else if (opt === '30') form.value.dueDate = new Date(now.getTime() + 30 * 24 * 60 * 60 * 1000).toISOString().slice(0, 10)
  else if (opt === 'eom') form.value.dueDate = new Date(now.getFullYear(), now.getMonth() + 1, 0).toISOString().slice(0, 10)
}
setDueOption('default')

const descriptionPlaceholder = computed(() => {
  const map = {
    PENALTY: 'Describe el daño o incumplimiento y cómo se calculó el monto...',
    SPECIAL_SERVICE: 'Describe el servicio prestado, cuándo y por quién...',
    BILLING_ADJUSTMENT: 'Explica el motivo del ajuste y cómo se calculó...',
    OTHER: 'Describe el concepto con suficiente detalle para que el inquilino lo entienda...',
  }
  return map[form.value.manualType] || 'Describe el concepto...'
})

const typeLabel = computed(() => {
  const map = { PENALTY: 'Penalidad o daño', SPECIAL_SERVICE: 'Servicio especial', BILLING_ADJUSTMENT: 'Ajuste de facturación', OTHER: form.value.manualTypeOther || 'Otro concepto' }
  return map[form.value.manualType] || ''
})

const finalAmount = computed(() => {
  if (!form.value.amount) return 0
  return form.value.includeIva ? form.value.amount * (1 + (form.value.ivaPercent || 16) / 100) : form.value.amount
})

const canSave = computed(() => !!form.value.contractId && !!form.value.manualType && !!form.value.amount && form.value.amount > 0 && !!form.value.dueDate)

// --- Adjuntos ---
const fileInputEl = ref(null)
const attachments = ref([])
function onFilesSelected(e) {
  attachments.value.push(...Array.from(e.target.files || []))
  e.target.value = ''
}

const submitting = ref(false)
async function onSubmit() {
  if (!canSave.value) return
  submitting.value = true
  try {
    const [year, month] = form.value.period.split('-').map(Number)
    const charge = await createManualCharge({
      contractId: form.value.contractId, periodYear: year, periodMonth: month,
      manualType: form.value.manualType, manualTypeOther: form.value.manualTypeOther,
      description: form.value.description, amount: form.value.amount, currency: form.value.currency,
      includeIva: form.value.includeIva, ivaPercent: form.value.ivaPercent, subtotal: form.value.includeIva ? form.value.amount : null,
      dueDate: form.value.dueDate, contractClauseRef: form.value.contractClauseRef,
      latePenaltyEnabled: form.value.latePenaltyEnabled, latePenaltyPercent: form.value.latePenaltyPercent, latePenaltyStartDate: form.value.latePenaltyStartDate || null,
      internalNote: form.value.internalNote, notifyTenant: form.value.notifyTenant, includeAttachmentsInStatement: form.value.includeAttachmentsInStatement,
    })

    for (const file of attachments.value) await uploadChargeAttachment(charge.id, file)

    emit('created', charge)
  } finally {
    submitting.value = false
  }
}

function onCloseAttempt() {
  const filledCount = [form.value.contractId, form.value.manualType, form.value.description, form.value.amount].filter(Boolean).length
  if (filledCount > 2 && !confirm('¿Descartar este cargo? Se perderá lo capturado.')) return
  emit('close')
}

function formatCurrency(n) { return new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN', maximumFractionDigits: 0 }).format(n || 0) }
function formatDate(iso) { return iso ? new Date(iso).toLocaleDateString('es-MX', { day: '2-digit', month: 'short', year: 'numeric' }) : '—' }
</script>

<style scoped>
.nmc-panel { flex: 0 0 35%; max-width: 35%; display: flex; flex-direction: column; background: #ffffff; border: 1px solid var(--border-active); border-radius: 10px; overflow: hidden; margin: 0.75rem 1.5rem 1.5rem 0; min-height: 0; }
.nmc-header { flex-shrink: 0; display: flex; align-items: center; padding: 0.85rem 1.25rem; border-bottom: 1px solid #f1f5f9; }
.nmc-title { display: block; font-family: var(--font-display); font-weight: 700; font-size: 15px; color: #1e3a5f; }
.nmc-subtitle { display: block; font-family: var(--font-body); font-size: 11px; color: #94a3b8; }
.nmc-close-btn { margin-left: auto; background: none; border: none; color: #94a3b8; cursor: pointer; display: flex; }

.nmc-content { flex: 1; min-height: 0; overflow-y: auto; padding: 1rem 1.25rem; }
.nmc-content::-webkit-scrollbar { width: 6px; }
.nmc-content::-webkit-scrollbar-thumb { background: var(--border-active); border-radius: 3px; }
.nmc-content::-webkit-scrollbar-track { background: transparent; }

.nmc-group { margin-bottom: 1.2rem; }
.nmc-group__header { display: flex; align-items: center; gap: 0.4rem; font-family: var(--font-body); font-size: 12px; color: #64748b; margin-bottom: 0.5rem; }
.nmc-group__number { width: 18px; height: 18px; border-radius: 50%; background: #3b82f6; color: #ffffff; font-size: 10px; display: flex; align-items: center; justify-content: center; flex-shrink: 0; }
.nmc-optional { font-family: var(--font-body); font-size: 10px; color: #94a3b8; margin-left: auto; }

.nmc-input, .nmc-textarea { width: 100%; font-family: var(--font-body); font-size: 13px; color: var(--text-content); background: #f8f9fc; border: 1px solid #e2e8f0; border-radius: 6px; padding: 0.45rem 0.6rem; box-sizing: border-box; margin-bottom: 0.5rem; }
.nmc-field-label { display: block; font-family: var(--font-body); font-size: 10px; text-transform: uppercase; color: #94a3b8; margin-bottom: 0.3rem; }

.nmc-tenant-search { position: relative; }
.nmc-tenant-results { background: #ffffff; border: 1px solid #e2e8f0; border-radius: 6px; margin-top: -0.4rem; margin-bottom: 0.5rem; overflow: hidden; }
.nmc-tenant-result { display: flex; flex-direction: column; align-items: flex-start; width: 100%; padding: 0.5rem 0.6rem; background: none; border: none; cursor: pointer; text-align: left; }
.nmc-tenant-result:hover { background: #f8fafc; }
.nmc-tenant-result-name { font-family: var(--font-body); font-size: 13px; font-weight: 500; color: #1e3a5f; }
.nmc-tenant-result-context { font-family: var(--font-body); font-size: 11px; color: #64748b; }

.nmc-tenant-card { display: flex; align-items: center; gap: 0.6rem; height: 48px; background: #eff6ff; border: 1px solid #bfdbfe; border-radius: 6px; padding: 0 0.6rem; margin-bottom: 0.5rem; }
.nmc-tenant-avatar { width: 32px; height: 32px; border-radius: 50%; color: #ffffff; font-size: 11px; font-weight: 600; display: flex; align-items: center; justify-content: center; flex-shrink: 0; }
.nmc-tenant-info { flex: 1; min-width: 0; }
.nmc-tenant-name { font-family: var(--font-body); font-size: 13px; font-weight: 500; color: #1e3a5f; }
.nmc-tenant-sub { font-family: var(--font-body); font-size: 11px; color: #64748b; }
.nmc-tenant-clear { background: none; border: none; color: #94a3b8; cursor: pointer; display: flex; }

.nmc-plot-pills, .nmc-pills, .nmc-type-grid { display: flex; flex-wrap: wrap; gap: 0.35rem; margin-bottom: 0.6rem; }
.nmc-type-grid { display: grid; grid-template-columns: 1fr 1fr; }
.nmc-pill { background: #ffffff; border: 1px solid #e2e8f0; border-radius: 20px; padding: 0.3rem 0.65rem; font-family: var(--font-body); font-size: 12px; color: #64748b; cursor: pointer; }
.nmc-pill--active { background: #eff6ff; border-color: #3b82f6; color: #3b82f6; font-weight: 500; }
.nmc-type-pill { display: flex; align-items: center; gap: 0.4rem; background: #ffffff; border: 1px solid #e2e8f0; border-radius: 8px; padding: 0.5rem; font-family: var(--font-body); font-size: 12px; color: #64748b; cursor: pointer; }
.nmc-type-pill--active { border-color: #3b82f6; background: #eff6ff; color: #1e3a5f; font-weight: 500; }

.nmc-amount-row { display: flex; gap: 0.4rem; margin-bottom: 0.5rem; }
.nmc-amount-input-wrap { flex: 1; display: flex; align-items: center; background: #ffffff; border: 1px solid #e2e8f0; border-radius: 6px; padding: 0 0.5rem; height: 40px; }
.nmc-amount-prefix { color: #94a3b8; margin-right: 0.2rem; }
.nmc-amount-input { border: none; outline: none; font-family: var(--font-mono); font-size: 16px; font-weight: 700; color: #1e3a5f; width: 100%; }
.nmc-currency-select { flex: 0 0 80px; margin-bottom: 0; }

.nmc-toggle-row { display: flex; align-items: center; gap: 0.4rem; font-family: var(--font-body); font-size: 12px; color: #1e3a5f; margin-bottom: 0.5rem; }
.nmc-iva-percent { width: 50px; margin-bottom: 0; padding: 0.2rem 0.4rem; }
.nmc-iva-breakdown { font-family: var(--font-body); font-size: 11px; color: #64748b; margin: -0.3rem 0 0.5rem; }

.nmc-penalty-fields { display: flex; gap: 0.4rem; }
.nmc-penalty-fields .nmc-input { flex: 1; }

.nmc-dropzone { border: 1px dashed #cbd5e1; background: #f8f9fc; border-radius: 8px; padding: 0.5rem 0.7rem; display: flex; align-items: center; gap: 0.6rem; cursor: pointer; margin-bottom: 0.5rem; }
.nmc-dropzone:hover { border-color: #94a3b8; }
.nmc-dropzone-icon { font-size: 15px; color: #94a3b8; flex-shrink: 0; }
.nmc-dropzone-text { display: flex; flex-direction: column; min-width: 0; }
.nmc-dropzone-title { font-family: var(--font-body); font-size: 11px; color: #475569; line-height: 1.3; }
.nmc-dropzone-hint { font-family: var(--font-body); font-size: 9.5px; color: #94a3b8; line-height: 1.3; }
.nmc-attachments { display: flex; flex-wrap: wrap; gap: 0.35rem; margin-bottom: 0.5rem; }
.nmc-attachment-pill { display: flex; align-items: center; gap: 0.3rem; background: #f1f5f9; border-radius: 20px; padding: 0.2rem 0.5rem; font-family: var(--font-body); font-size: 10px; color: #64748b; }
.nmc-attachment-pill .mdi { cursor: pointer; font-size: 10px; }

.nmc-preview { background: #f8f9fc; border: 1px solid #e2e8f0; border-radius: 8px; padding: 0.6rem; margin-top: 0.5rem; }
.nmc-preview__content { display: flex; align-items: center; gap: 0.5rem; flex-wrap: wrap; }
.nmc-preview__type-tag { font-size: 10px; border-radius: 4px; padding: 0.1rem 0.4rem; font-weight: 500; }
.nmc-preview__type-tag--PENALTY { background: #fee2e2; color: #dc2626; }
.nmc-preview__type-tag--SPECIAL_SERVICE { background: #dbeafe; color: #2563eb; }
.nmc-preview__type-tag--BILLING_ADJUSTMENT { background: #fef3c7; color: #d97706; }
.nmc-preview__type-tag--OTHER { background: #f1f5f9; color: #64748b; }
.nmc-preview__desc { flex: 1; font-family: var(--font-body); font-size: 11px; color: #64748b; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.nmc-preview__amount { font-family: var(--font-mono); font-weight: 700; font-size: 13px; color: #1e3a5f; }
.nmc-preview__due { font-family: var(--font-body); font-size: 10px; color: #94a3b8; }
.nmc-preview__hint { display: flex; align-items: center; gap: 0.3rem; font-family: var(--font-body); font-size: 10px; color: #3b82f6; margin-top: 0.4rem; }

.nmc-footer { flex-shrink: 0; display: flex; align-items: center; gap: 0.6rem; padding: 0.85rem 1.25rem; border-top: 1px solid #e2e8f0; }
.nmc-cancel-btn { flex: 0 0 35%; background: #ffffff; border: 1px solid #e2e8f0; border-radius: 6px; height: 38px; font-family: var(--font-body); font-size: 13px; font-weight: 500; color: #1e3a5f; cursor: pointer; }
.nmc-submit-wrap { flex: 1; display: flex; flex-direction: column; gap: 0.2rem; }
.nmc-submit-btn { width: 100%; background: #3b82f6; border: none; border-radius: 6px; height: 38px; font-family: var(--font-body); font-size: 13px; font-weight: 500; color: #ffffff; cursor: pointer; box-shadow: 0 0 12px rgba(59,130,246,0.4); }
.nmc-submit-btn:disabled { opacity: 0.4; cursor: not-allowed; box-shadow: none; }
.nmc-submit-hint { font-family: var(--font-body); font-size: 10px; color: #94a3b8; text-align: center; }
</style>